import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useFormik } from 'formik';
import { Button, InputBar, Textarea, GhostPicture } from '~/ui/index';
import UploadPhotoItem from '~/components/UploadPhoto/UploadPhotoItem/UploadPhotoItem';
import styles from './RequestForm.module.scss';
import { VideoPlayer } from '~/components/Shared/VideoPlayer/VideoPlayer';
import { useTranslation } from 'react-i18next';
import { getTimeDates } from '~/utils/getTimeDates';
import { SectionLink } from '~/ui/SectionLink/SectionLink';

export interface Photo {
  id: number;
  full_photo_url: string;
}

interface Video {
  id: number;
  full_video_url: string;
  full_preview_video_url: string;
}

export interface RequestFormItem {
  id: number;
  category_name: string;
  object_name: string;
  description: string;
  ticket_status: number;
  user_name: string;
  grade_name: string;
  created_at: string;
  user_uniqueId: string;
  ticket_photo: Photo[];
  ticket_video: Video[];
  business_trip: {
    accommodation: { name: string; section: string };
    room: { name: string };
  };
  working_period: string | null;
  user_id: number;
  review: string | null;
}

interface RequestFormProps {
  request: RequestFormItem;
  solution: string;
  setSolution: (solution: string) => void;
  updateTicketSolution: ({
    id,
    review,
  }: {
    id: number;
    review: string;
  }) => void;
}

export const RequestForm = ({
  request,
  setSolution,
  updateTicketSolution,
}: RequestFormProps) => {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { t } = useTranslation();

  const photos = request?.ticket_photo;
  const name = request?.business_trip?.accommodation?.name || '';
  const room = request?.business_trip?.room?.name || '';
  const section = request?.business_trip?.accommodation?.section || '';
  const review = request?.review || '';

  const handleUpdate = () => {
    updateTicketSolution({
      id: request.id,
      review: formik.values.solution,
    });
  };

  const finallyInfo = `${name ? name + '/' : ''} ${
    section ? section + '/' : ''
  } ${room ? room : ''}`;

  const formik = useFormik({
    initialValues: {
      category: '',
      object_name: '',
      comments: '',
      status: '',
      user_name: '',
      grade: '',
      date: '',
      uniqueID: '',
      working_period: '',
      solution: '',
    },

    onSubmit: () => {
      formik.resetForm();
    },
  });

  useEffect(() => {
    if (!request) return;

    formik.setValues({
      category: request.category_name,
      object_name: request.object_name,
      comments: request.description,
      status: request.ticket_status.toString(),
      user_name: request.user_name,
      grade: request.grade_name,
      date: getTimeDates(request.created_at),
      uniqueID: request.user_uniqueId,
      working_period: request.working_period || '',
      solution: request.review || '',
    });
  }, [request]);

  return (
    <div className={styles.formWrapper}>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formItems}>
          <div className={styles.inputWrapper}>
            <InputBar
              name="category"
              value={formik.values.category || t('not_specified')}
              disabled={true}
              readOnly={true}
              label={t('category')}
            />
            <InputBar
              name="working_period"
              value={formik.values.working_period || `(${t('empty')})`}
              disabled={true}
              readOnly={true}
              label={t('shift')}
            />
          </div>

          <InputBar
            name="object"
            value={formik.values.object_name || t('not_specified')}
            disabled={true}
            readOnly={true}
            label={t('object_place')}
          />

          <InputBar
            name="createdOn"
            value={
              request.business_trip && finallyInfo
                ? finallyInfo
                : t('not_specified')
            }
            disabled={true}
            readOnly={true}
            label={`${t('application_created_on')}:`}
          />

          <InputBar
            name="uniqueID"
            value={formik.values.uniqueID}
            disabled={true}
            readOnly={true}
            label={t('unique_user_ID')}
          />

          <div className={styles.infoWrapper}>
            <InputBar
              name="user_name"
              value={formik.values.user_name}
              disabled={true}
              readOnly={true}
              label={t('employee')}
              sectionLink={
                <SectionLink
                  title={t('to_contact')}
                  link={`/user/${request?.user_id}`}
                />
              }
            />

            <InputBar
              name="grade"
              value={formik.values.grade}
              disabled={true}
              readOnly={true}
              label={t('access_group')}
            />

            <InputBar
              name="date"
              value={formik.values.date}
              disabled={true}
              readOnly={true}
              label={t('date_and_time')}
            />
          </div>

          <Textarea
            showCounter
            rows={6}
            label={t('description')}
            placeholder={t('leave_comment')}
            maxLength={1000}
            value={formik.values.comments}
            onChange={formik.handleChange}
            name="comments"
            star={false}
            disabled={true}
            readOnly={true}
          />

          <Textarea
            label={t('solution')}
            name="solution"
            placeholder={t('solution_placeholder')}
            value={formik?.values?.solution}
            onChange={e => {
              formik.setFieldValue('solution', e.target.value);
              setSolution(e.target.value);
            }}
            showCounter
            rows={6}
            maxLength={1000}
            star={false}
          />

          <div className={styles.mediaContainer}>
            <div className={styles.imgContainer}>
              {photos?.length === 0 && <GhostPicture />}
              {photos?.map(({ id, full_photo_url }) => {
                return (
                  <UploadPhotoItem
                    key={id}
                    url={full_photo_url}
                    deletePhoto={false}
                  />
                );
              })}
            </div>
            {request
              ? request.ticket_video.map(
                  ({ id, full_video_url, full_preview_video_url }) => (
                    <VideoPlayer
                      key={id}
                      url={full_video_url}
                      poster={full_preview_video_url}
                    />
                  )
                )
              : null}
          </div>
        </div>

        {review === formik.values.solution ? (
          <Button
            type="submit"
            text={t('back_to_list')}
            className={styles.btn}
            onClick={goBack}
            disabled={false}
          />
        ) : (
          <Button
            type="button"
            text={t('save_n_exit')}
            className={styles.btn}
            onClick={handleUpdate}
            disabled={false}
          />
        )}
      </form>
    </div>
  );
};
